import axios from 'axios';

import { API_BASE, LANDING_AGENCY_ID } from '@/lib/env';

const api = axios.create({
  baseURL: API_BASE,
});

// Set Content-Type only for non-FormData requests; add auth token
api.interceptors.request.use((config) => {
  if (!(config.data instanceof FormData)) {
    config.headers.set('Content-Type', 'application/json');
  }
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// On 401, clear token and redirect to login — except for the auth attempts
// themselves (login y Google), donde el 401 significa "credencial inválida"
// y la UI debe mostrar su mensaje sin recargar la página
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url: string = error.config?.url ?? '';
    const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/google');
    if (error.response?.status === 401 && !isAuthAttempt) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (email: string, password: string, inviteCode?: string, agencyInviteToken?: string) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      // Beta cerrada (auth 007): el backend decide si lo exige.
      // trim + vacío→null: normalización consistente entre callers
      invite_code: inviteCode?.trim() || null,
      // Token del link de invitación de agencia: sustituye al código beta
      agency_invite_token: agencyInviteToken || null,
    });
    return response.data;
  },

  // Verificación de email (auth 007): link (token, anónimo) XOR código (sesión)
  verifyEmail: async (payload: { token?: string; code?: string }) => {
    const response = await api.post('/auth/verify-email', payload);
    return response.data;
  },

  resendVerification: async () => {
    const response = await api.post('/auth/resend-verification');
    return response.data;
  },

  // Sign in with Google (auth 007): 200 login / 201 cuenta nueva
  googleLogin: async (payload: {
    id_token: string;
    invite_code?: string | null;
    privacy_accepted?: boolean;
    terms_accepted?: boolean;
    agency_invite_token?: string | null;
  }) => {
    const response = await api.post('/auth/google', {
      ...payload,
      // Normalización consistente entre callers (el backend también hace strip)
      invite_code: payload.invite_code?.trim() || null,
    });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/me');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  },
};

// Properties service
export const propertiesService = {
  search: async (filters: Record<string, unknown>) => {
    const response = await api.post('/properties/search', filters);
    return response.data;
  },

  uploadImage: async (propertyId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/properties/${propertyId}/images`, formData);
    return response.data as { images: string[] };
  },

  deleteImage: async (propertyId: string, index: number) => {
    const response = await api.delete(`/properties/${propertyId}/images`, {
      params: { index },
    });
    return response.data as { images: string[] };
  },

  reorderImages: async (propertyId: string, order: number[]) => {
    const response = await api.put(`/properties/${propertyId}/images/order`, { order });
    return response.data as { images: string[] };
  },

  getById: async (propertyId: string) => {
    const response = await api.get(`/properties/${propertyId}`);
    return response.data;
  },
};

// Rentals service
export const rentalsService = {
  getDashboard: async () => {
    const response = await api.get('/rentals/dashboard');
    return response.data;
  },
  listOverdue: async () => {
    const response = await api.get('/rentals/overdue');
    return response.data;
  },
  listLeases: async <T = Record<string, unknown>>(params?: {
    status?: string;
    property_id?: string;
  }) => {
    const response = await api.get('/leases', { params });
    return response.data as { total: number; items: T[] };
  },
  getLease: async (id: string) => {
    const response = await api.get(`/leases/${id}`);
    return response.data;
  },
  createLease: async (data: Record<string, unknown>) => {
    const response = await api.post('/leases', data);
    return response.data;
  },
  updateLeaseStatus: async (id: string, status: string) => {
    const response = await api.patch(`/leases/${id}/status`, { status });
    return response.data;
  },
  markPaid: async (leaseId: string, periodId: string, data: Record<string, unknown> = {}) => {
    const response = await api.post(`/leases/${leaseId}/rent-periods/${periodId}/mark-paid`, data);
    return response.data;
  },
  addIncrease: async (leaseId: string, data: Record<string, unknown>) => {
    const response = await api.post(`/leases/${leaseId}/rent-increases`, data);
    return response.data;
  },
  listUnits: async (propertyId: string) => {
    const response = await api.get(`/properties/${propertyId}/units`);
    return response.data as Array<{ id: string; name: string; status: string }>;
  },
  createUnit: async (propertyId: string, data: Record<string, unknown>) => {
    const response = await api.post(`/properties/${propertyId}/units`, data);
    return response.data;
  },
  listTenants: async <T = Record<string, unknown>>() => {
    const response = await api.get('/rental-tenants');
    return response.data as { total: number; items: T[] };
  },
  createTenant: async (data: Record<string, unknown>) => {
    const response = await api.post('/rental-tenants', data);
    return response.data;
  },
};

// Client views service
export const clientViewsService = {
  list: async () => {
    const response = await api.get('/client-views');
    return response.data as Array<{ id: string; title: string; created_at: string; items: Array<{ id: string; property_id: string }>; notes: Array<unknown> }>;
  },

  create: async (title: string, propertyIds: string[]) => {
    const response = await api.post('/client-views', { title, property_ids: propertyIds });
    return response.data;
  },
  
  get: async (id: string) => {
    const response = await api.get(`/client-views/${id}`);
    return response.data;
  },
  
  addNote: async (clientViewId: string, propertyId: string | null, content: string) => {
    const response = await api.post(`/client-views/${clientViewId}/notes`, {
      property_id: propertyId || null,
      content,
    });
    return response.data;
  },

  generateShareLink: async (clientViewId: string, expiresInHours: number = 168) => {
    const response = await api.post(`/client-views/${clientViewId}/share-links`, {
      expires_in_hours: expiresInHours,
    });
    return response.data;
  },
};

// Agency service
export const agencyService = {
  create: async (name: string) => {
    const response = await api.post('/agencies', { name });
    return response.data as { id: string; name: string; slug: string; plan: string };
  },

  invite: async (agencyId: string, email: string, role: 'admin' | 'agent' = 'agent') => {
    const response = await api.post(`/agencies/${agencyId}/invite`, { email, role });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/agencies/me');
    return response.data as { agency: { id: string; name: string; slug: string; plan: string }; role: string; member_id: string };
  },

  getMembers: async (agencyId: string) => {
    const response = await api.get(`/agencies/${agencyId}/members`);
    return response.data as { members: Array<{ id: string; agency_id: string; user_id: string; role: string }> };
  },
};

// Client portal service (uses client:access token stored separately)
const clientApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});
clientApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
clientApi.interceptors.response.use(
  (r) => r,
  (error) => {
    const isAccessRequest = error.config?.url?.includes('/clients/access');
    if (error.response?.status === 401 && !isAccessRequest) {
      localStorage.removeItem('client_access_token');
      window.location.href = '/portal/login';
    }
    return Promise.reject(error);
  }
);

export const interactionService = {
  record: async (propertyId: string, type: 'viewed' | 'liked' | 'rejected') => {
    const response = await clientApi.post('/interactions', { property_id: propertyId, type });
    return response.data;
  },
  getActivity: async (clientId: string) => {
    const response = await clientApi.get(`/clients/${clientId}/activity`);
    return response.data as Array<{ id: string; property_id: string; type: string; created_at: string }>;
  },
  getPreferences: async (clientId: string) => {
    const response = await clientApi.get(`/clients/${clientId}/preferences`);
    return response.data;
  },
  updatePreferences: async (clientId: string, prefs: {
    price_min?: number | null;
    price_max?: number | null;
    bedrooms_min?: number | null;
    bathrooms_min?: number | null;
    area_min?: number | null;
    zones?: string[] | null;
  }) => {
    const response = await clientApi.put(`/clients/${clientId}/preferences`, prefs);
    return response.data;
  },
};

export const clientService = {
  login: async (agencyId: string, email: string, password: string) => {
    const response = await axios.post(
      `${API_BASE}/clients/access?agency_id=${agencyId}`,
      { email, password }
    );
    return response.data as { access_token: string; token_type: string; expires_in: number };
  },

  getMe: async () => {
    const response = await clientApi.get('/clients/me');
    return response.data;
  },

  create: async (data: {
    email: string;
    name: string;
    password: string;
    phone?: string;
    assigned_agent_id?: string;
  }) => {
    const response = await api.post('/clients', data);
    return response.data;
  },

  list: async () => {
    const response = await api.get('/clients');
    return response.data as Array<{
      id: string;
      agency_id: string;
      email: string;
      name: string;
      phone: string | null;
      assigned_agent_id: string | null;
    }>;
  },

  getPortalProperties: async (limit = 50, offset = 0) => {
    const response = await clientApi.get('/clients/portal/properties', {
      params: { limit, offset },
    });
    return response.data as { total: number; items: Array<{
      id: string; title: string; price: number; currency: string;
      address: string; bedrooms: number; bathrooms: number; area: number;
      lat: number; lng: number;
    }> };
  },

  logout: () => {
    localStorage.removeItem('client_access_token');
    window.location.href = '/portal/login';
  },
};

// Public views service (no auth required)
export const publicViewsService = {
  get: async (token: string) => {
    const response = await axios.get(`${API_BASE}/public/views/${token}`);
    return response.data;
  },
};

// Invites service (info is public; accept requires the freshly obtained auth token)
export const invitesService = {
  getInfo: async (token: string) => {
    const response = await axios.get(`${API_BASE}/invites/${token}`);
    return response.data as {
      invite_id: string;
      agency_id: string;
      email: string;
      role: 'admin' | 'agent';
    };
  },

  accept: async (token: string) => {
    const response = await api.post(`/invites/${token}/accept`);
    return response.data as { id: string; agency_id: string; user_id: string; role: string };
  },
};

// Public agency info (no auth required)
export const publicAgencyService = {
  get: async (agencyId: string) => {
    const response = await axios.get(`${API_BASE}/public/agencies/${agencyId}`);
    return response.data as { id: string; name: string; slug: string };
  },
};

// Public leads (no auth required): landing contact form + public search
export const publicLeadsService = {
  create: async (payload: {
    agency_id: string;
    source: 'direct' | 'search';
    source_property_id?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    search_criteria?: Record<string, unknown> | null;
  }) => {
    const response = await axios.post(`${API_BASE}/public/leads`, payload);
    return response.data;
  },
  submit: async (data: {
    name: string;
    email?: string | null;
    phone?: string | null;
    message?: string | null;
  }) => {
    if (!LANDING_AGENCY_ID) {
      throw new Error('VITE_LANDING_AGENCY_ID is not configured');
    }
    return publicLeadsService.create({
      agency_id: LANDING_AGENCY_ID,
      source: 'direct',
      name: data.name,
      email: data.email || null,
      phone: data.phone || null,
      search_criteria: data.message ? { message: data.message } : null,
    });
  },
};

export default api;
